// Using local storage for now instead of API client due to generated API issues

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
  id?: string;

  model?: any;
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  stream?: boolean;
  temperature?: number;
  top_p?: number;
  top_k?: number;
  max_tokens?: number;
}

export interface ChatCompletionResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: ChatMessage;
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  inference_id?: string;
}

export interface ChatCompletionStreamChunk {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: {
      role?: string;
      content?: string;
      reasoning_content?: string;
      reasoning?: string;
      thinking?: string;
    };
    finish_reason?: string;
  }>;
  inference_id?: string;
}

/**
 * Service for handling chat completions with the Swarmind API
 */
export class ChatService {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly namespace: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    this.apiKey =
      localStorage.getItem('bearerToken') ||
      process.env.REACT_APP_API_KEY ||
      '';
    this.namespace = 'frontend';
  }

  /**
   * Send a chat completion request (non-streaming)
   */
  async sendChatCompletion(
    request: ChatCompletionRequest
  ): Promise<ChatCompletionResponse> {
    const url = `${this.baseUrl}/lai/testing/v1/chat/completions`;

    const requestBody = {
      ...request,
      stream: false,
      namespace: this.namespace,
    };

    console.log('🚀 Sending non-streaming chat request:', {
      url,
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey
          ? { Authorization: `Bearer ${this.apiKey.substring(0, 10)}...` }
          : {}),
      },
      body: requestBody,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Non-streaming API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText,
      });
      const err: any = new Error(
        `Chat completion failed: ${response.status} ${response.statusText}`
      );
      if (response.status === 422) {
        const lower = (errorText || '').toLowerCase();
        if (
          lower.includes('balance') ||
          lower.includes('insufficient') ||
          lower.includes('not enough')
        ) {
          err.code = 'INSUFFICIENT_BALANCE';
          err.details = errorText;
        } else if (
          lower.includes('no rows in result set') ||
          lower.includes('querying model id')
        ) {
          err.code = 'MODEL_NOT_FOUND';
          err.details = errorText;
        }
      }
      throw err;
    }

    const inferenceId = response.headers.get('inference-id');
    console.log(
      '🔍 Captured inference-id from headers (non-streaming):',
      inferenceId
    );

    const responseData = await response.json();

    if (inferenceId) {
      responseData.inference_id = inferenceId;
    }

    return responseData;
  }

  /**
   * Send a streaming chat completion request
   */
  async *sendStreamingChatCompletion(
    request: ChatCompletionRequest
  ): AsyncGenerator<ChatCompletionStreamChunk> {
    const url = `${this.baseUrl}/lai/testing/v1/chat/completions`;

    const requestBody = {
      ...request,
      stream: true,
      namespace: this.namespace,
    };

    console.log('🚀 Sending chat request:', {
      url,
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey
          ? { Authorization: `Bearer ${this.apiKey.substring(0, 10)}...` }
          : {}),
      },
      body: requestBody,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText,
      });
      const err: any = new Error(
        `Streaming chat completion failed: ${response.status} ${response.statusText}`
      );
      if (response.status === 422) {
        const lower = (errorText || '').toLowerCase();
        if (
          lower.includes('balance') ||
          lower.includes('insufficient') ||
          lower.includes('not enough')
        ) {
          err.code = 'INSUFFICIENT_BALANCE';
          err.details = errorText;
        } else if (
          lower.includes('no rows in result set') ||
          lower.includes('querying model id')
        ) {
          err.code = 'MODEL_NOT_FOUND';
          err.details = errorText;
        }
      }
      throw err;
    }

    const inferenceId = response.headers.get('inference-id');
    console.log('🔍 Captured inference-id from headers:', inferenceId);

    const decoder = new TextDecoderStream();
    const reader = response.body?.pipeThrough(decoder)?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === '') {
            continue;
          }

          if (trimmedLine === 'data: [DONE]') {
            return;
          }

          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6);
              const chunk = JSON.parse(jsonStr) as ChatCompletionStreamChunk;
              chunk.inference_id = inferenceId;
              yield chunk;
            } catch (error) {
              console.warn('Failed to parse SSE chunk:', trimmedLine, error);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Save chat history to backend store; fallback to local storage on failure
   */
  async saveChatHistory(
    namespace: string,
    chatId: string,
    messages: ChatMessage[]
  ): Promise<void> {
    // Try backend first
    try {
      await this.updateServerHistory(namespace, chatId, messages);
      return;
    } catch (error) {
      console.warn('Falling back to local chat history save:', error);
      try {
        const key = `chat_${namespace}_${chatId}`;
        const data = {
          messages,
          lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) {
        console.error('Failed to save chat history locally:', e);
        throw e;
      }
    }
  }

  /**
   * Load chat history from backend store; fallback to local storage on failure
   */
  async loadChatHistory(
    namespace: string,
    chatId: string
  ): Promise<ChatMessage[]> {
    // Try backend first
    try {
      const server = await this.getServerHistory(namespace, chatId);
      if (server && server.length > 0) {
        // Try to fetch per-message model pairs and annotate messages
        try {
          const pairs = await this.getServerModelPairs(namespace, chatId);
          if (pairs && typeof pairs === 'object') {
            const annotated = server.map(msg =>
              msg?.id && pairs[msg.id] ? { ...msg, model: pairs[msg.id] } : msg
            );
            return annotated;
          }
        } catch (e) {
          console.warn('Failed to load model pairs for chat:', e);
        }
        return server;
      }
    } catch (error) {
      console.warn(
        'Failed to load server history, trying local fallback:',
        error
      );
    }

    try {
      const key = `chat_${namespace}_${chatId}`;
      const data = localStorage.getItem(key);
      if (!data) return [];
      const parsed = JSON.parse(data);
      return (parsed.messages || []).map((msg: any) => ({
        ...msg,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
      }));
    } catch (e) {
      console.error('Failed to load chat history locally:', e);
      return [];
    }
  }

  /**
   * Get list of chats for a namespace (simplified for now)
   */
  async getChatList(namespace: string): Promise<string[]> {
    // Try backend list first
    try {
      const url = `${this.baseUrl}/store/chats/${encodeURIComponent(namespace)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data) ? (data as string[]) : [];
      }
    } catch (e) {
      console.warn('Failed to get server chat list, using local:', e);
    }

    // Local fallback
    const chats: string[] = [];
    const prefix = `chat_${namespace}_`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const chatId = key.substring(prefix.length);
        chats.push(chatId);
      }
    }
    return chats;
  }

  /**
   * Delete a chat (tries backend, then local)
   */
  async deleteChat(namespace: string, chatId: string): Promise<void> {
    // Try server
    try {
      const url = `${this.baseUrl}/store/history/${encodeURIComponent(namespace)}/${encodeURIComponent(chatId)}`;
      await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
        },
        credentials: 'include',
      });
    } catch (e) {
      console.warn('Failed to delete server chat history:', e);
    }

    // Always try local cleanup
    const key = `chat_${namespace}_${chatId}`;
    localStorage.removeItem(key);
  }

  /**
   * Generate a unique message ID
   */
  public generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate a unique chat ID
   */
  generateChatId(): string {
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Convert blockchain model ID to API model name
   */
  getModelNameFromId(modelId: string): string {
    console.log('🔄 Converting model ID:', modelId);

    console.log('✅ Using original model ID:', modelId);
    return modelId;
  }

  /**
   * Resolve selected model id to backend model name using /models list
   */
  async resolveApiModelName(modelId?: string | null): Promise<string> {
    try {
      if (!modelId) {
        const { modelsService } = await import('./modelsService');
        const list = await modelsService.getAvailableModels();
        const first = list[0];
        const chosen = (
          first?.name ||
          first?.hfid ||
          first?.id ||
          ''
        ).toString();
        return chosen.trim();
      }
      const { modelsService } = await import('./modelsService');
      const model = await modelsService.getModelById(modelId);
      const chosen = (
        model?.name ||
        model?.hfid ||
        model?.id ||
        modelId
      ).toString();
      return chosen.trim();
    } catch (_) {
      return modelId;
    }
  }

  // ===== Store API integration =====

  private chatMessageToStore(msg: ChatMessage) {
    const created = msg.timestamp || new Date();
    // Don't include model field in message data - models are tracked separately via /store/models/pairs
    const messageData: any = {
      role: msg.role,
      content: msg.content,
      parts: [
        {
          type: 'text',
          text: msg.content,
        },
      ],
    };

    return {
      id: msg.id || this.generateMessageId(),
      created_at: created.toISOString(),
      updated_at: created.toISOString(),
      message: messageData,
    } as any;
  }

  private storeToChatMessage(rec: any): ChatMessage {
    const role = rec?.message?.role || 'assistant';
    const message = rec?.message || {};

    let content = '';

    // Handle different message formats - prioritize 'content' field
    if (message.content) {
      // Direct content field (standard format)
      content = message.content;
    } else if (message.text) {
      // Legacy text field (old format)
      content = message.text;
    } else if (message.parts && Array.isArray(message.parts)) {
      // Parts array format
      content = message.parts
        .map((part: any) => {
          if (typeof part === 'string') return part;
          if (part?.type === 'text') return part.text || '';
          return '';
        })
        .join('');
    }

    // Debug logging to understand the data structure
    if (!content && rec?.message) {
      console.warn('Failed to extract content from message:', {
        role,
        message: rec.message,
        rec: rec,
      });
    }

    return {
      id: rec?.id,
      role,
      content: content || 'No content available',
      timestamp: rec?.created_at ? new Date(rec.created_at) : new Date(),
    };
  }

  private async getServerHistory(
    namespace: string,
    chatId: string
  ): Promise<ChatMessage[]> {
    const url = `${this.baseUrl}/store/history/${encodeURIComponent(namespace)}/${encodeURIComponent(chatId)}`;
    console.log('🔍 Fetching chat history from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });

    console.log('📡 Chat history response status:', response.status);

    if (!response.ok) {
      throw new Error(`Failed to get history: ${response.status}`);
    }

    const data = await response.json();
    console.log('📦 Raw chat history data:', data);
    console.log('📊 Data type:', typeof data, 'Is array:', Array.isArray(data));

    if (!Array.isArray(data)) {
      console.warn('⚠️ Expected array but got:', typeof data, data);
      return [];
    }

    const parsedMessages = data.map((rec: any, index: number) => {
      console.log(`🔍 Parsing message ${index}:`, rec);
      const parsed = this.storeToChatMessage(rec);
      console.log(`✅ Parsed message ${index}:`, parsed);
      return parsed;
    });

    console.log('🎯 Final parsed messages:', parsedMessages);
    return parsedMessages;
  }

  /**
   * Fetch per-message model mapping for a chat
   */
  private async getServerModelPairs(
    namespace: string,
    chatId: string
  ): Promise<Record<string, any>> {
    const url = `${this.baseUrl}/store/models/pairs/${encodeURIComponent(namespace)}/${encodeURIComponent(chatId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to get model pairs: ${response.status}`);
    }
    const data = await response.json();
    return data && typeof data === 'object'
      ? (data as Record<string, any>)
      : {};
  }

  private async updateServerHistory(
    namespace: string,
    chatId: string,
    messages: ChatMessage[]
  ): Promise<void> {
    const url = `${this.baseUrl}/store/history/${encodeURIComponent(namespace)}/${encodeURIComponent(chatId)}`;
    const payload = {
      messages: messages.map(m => this.chatMessageToStore(m)),
    } as any;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok && response.status !== 204) {
      // Some servers return 204 No Content on success
      const text = await response.text().catch(() => '');
      throw new Error(`Failed to update history: ${response.status} ${text}`);
    }
  }

  // ===== Address management =====

  /**
   * Send wallet address to backend (called when wallet connects or disconnects)
   */
  public async setWalletAddress(walletAddress: string): Promise<void> {
    // Allow empty string for disconnection, otherwise validate format
    if (walletAddress && !walletAddress.startsWith('0x')) {
      throw new Error('Invalid wallet address provided');
    }

    console.log('🔗 Setting wallet address on backend:', walletAddress);
    await this.postServerAddress(walletAddress);
  }
  public async deleteWalletAddress(): Promise<void> {
    // Allow empty string for disconnection, otherwise validate format
    console.log('🔗 Deleting wallet address on backend:');
    await this.deleteServerAddress();
  }

  /**
   * Check if user has a valid address set on backend
   */
  private async ensureUserAddress(walletAddress?: string): Promise<void> {
    try {
      const current = await this.getServerAddress();
      if (current && current.address && current.address.length >= 26) return;
    } catch (_) {
      // proceed to set
    }

    // Require actual MetaMask address - no fallback
    if (!walletAddress || !walletAddress.startsWith('0x')) {
      throw new Error(
        'MetaMask wallet address is required. Please connect your MetaMask wallet to continue.'
      );
    }

    console.log('🔗 Using MetaMask address:', walletAddress);
    await this.postServerAddress(walletAddress);
  }

  private async getServerAddress(): Promise<{ address?: string } | null> {
    const url = `${this.baseUrl}/address`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });
    if (!res.ok) return null;
    return res.json();
  }

  private async postServerAddress(address: string): Promise<void> {
    const url = `${this.baseUrl}/address`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ address }),
    });
    if (!res.ok && res.status !== 204) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to set address: ${res.status} ${text}`);
    }
  }

  private async deleteServerAddress(): Promise<void> {
    const url = `${this.baseUrl}/address`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      credentials: 'include',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to set address: ${res.status} ${text}`);
    }
  }
}

export const chatService = new ChatService();
